import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ImageLoader } from '../Game/ImageLoader';
import {
  getItems,
  getItemTypes,
  getRarity,
  getAdminUsers,
  getAdminUserInventory,
  grantAdminUserItem,
  updateAdminUser,
  updateAdminUserInventoryItem,
  updateAdminUserPassword,
} from './AdminAPI';
import SearchPickerDialog from './WorldEditor/SearchPickerDialog';

const PAGE_SIZE = 25;
const ACCOUNT_STATES = [
  {
    value: 555,
    label: 'Pre-registered (locked)',
    detail: 'The account exists but cannot enter the game.',
  },
  {
    value: 1,
    label: 'Player',
    detail: 'Standard live-game access.',
  },
  {
    value: 2,
    label: 'Beta player',
    detail: 'Live-game and PTS access.',
  },
  {
    value: 100,
    label: 'Administrator',
    detail: 'Playable account with Admin access.',
  },
  {
    value: 0,
    label: 'Guest (locked)',
    detail: 'Reserved guest state without game access.',
  },
  {
    value: 666,
    label: 'Banned',
    detail: 'Sign-in and game access are blocked.',
  },
];

const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);

function roleValue(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const named = {
    guest: 0,
    user: 1,
    betaUser: 2,
    admin: 100,
    notActivated: 555,
    banned: 666,
  };
  return named[value] ?? 555;
}

function stateFor(role) {
  return ACCOUNT_STATES.find(state => state.value === roleValue(role))
    || ACCOUNT_STATES[0];
}

function normalizeUser(value) {
  return {
    id: String(read(value, 'id', 'Id') || ''),
    name: String(read(value, 'name', 'Name') || ''),
    email: String(read(value, 'email', 'Email') || ''),
    userRole: roleValue(read(value, 'userRole', 'UserRole')),
    canPlay: Boolean(read(value, 'canPlay', 'CanPlay')),
    xp: Number(read(value, 'xp', 'Xp')) || 0,
    lastLogin: Number(read(value, 'lastLogin', 'LastLogin')) || 0,
    lastActivity: Number(read(value, 'lastActivity', 'LastActivity')) || 0,
  };
}

function normalizePage(value) {
  const pageUsers = read(value, 'users', 'Users');
  return {
    users: Array.isArray(pageUsers) ? pageUsers.map(normalizeUser) : [],
    totalCount: Number(read(value, 'totalCount', 'TotalCount')) || 0,
    overallCount: Number(read(value, 'overallCount', 'OverallCount')) || 0,
  };
}

function normalizeInventory(value) {
  const items = read(value, 'items', 'Items');
  return {
    capacity: Number(read(value, 'capacity', 'Capacity')) || 25,
    items: (Array.isArray(items) ? items : []).map(item => ({
      itemId: String(read(item, 'itemId', 'ItemId') || ''),
      itemInstanceId: String(
        read(item, 'itemInstanceId', 'ItemInstanceId') || '',
      ),
      itemName: String(read(item, 'itemName', 'ItemName') || 'Item'),
      imageId: String(read(item, 'imageId', 'ImageId') || ''),
      animatedMediaId: String(
        read(item, 'animatedMediaId', 'AnimatedMediaId') || '',
      ),
      isStackable: Boolean(read(item, 'isStackable', 'IsStackable')),
      quantity: Number(read(item, 'quantity', 'Quantity')) || 0,
      x: Number(read(item, 'x', 'X')) || 0,
      y: Number(read(item, 'y', 'Y')) || 0,
    })),
  };
}

function formatTimestamp(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

function PackItemEditor({ busy, item, onSave }) {
  const [quantity, setQuantity] = useState(item.quantity);
  const fieldId = `admin-pack-${item.x}-${item.y}-quantity`;

  useEffect(() => {
    setQuantity(item.quantity);
  }, [item.quantity]);

  return (
    <li className="admin-users__pack-item">
      <ImageLoader
        alt=""
        animatedMediaId={item.animatedMediaId}
        className="admin-users__pack-image"
        imageId={item.imageId}
        itemId={item.itemId}
        size="small"
      />
      <div className="admin-users__pack-item-name">
        <strong>{item.itemName}</strong>
        <small>Slot {item.x + 1}, {item.y + 1}</small>
        {item.itemInstanceId && <small>{item.itemInstanceId}</small>}
      </div>
      <div className="admin-users__pack-actions">
        <label htmlFor={fieldId}>
          Quantity
          <input
            aria-label={`Quantity for ${item.itemName} in slot ${item.x + 1}, ${item.y + 1}`}
            id={fieldId}
            max={item.isStackable ? 20 : 1}
            min="1"
            onChange={event => setQuantity(Number(event.target.value))}
            readOnly={!item.isStackable}
            type="number"
            value={quantity}
          />
        </label>
        <button
          className="button-secondary"
          disabled={busy || quantity === item.quantity}
          onClick={() => onSave(item, quantity)}
          type="button"
        >
          Save
        </button>
        <button
          className="button-secondary admin-users__remove-item"
          disabled={busy}
          onClick={() => onSave(item, 0)}
          type="button"
        >
          Remove
        </button>
      </div>
    </li>
  );
}

export default function AdminUsers() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [overallCount, setOverallCount] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState({ email: '', userRole: 555 });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [grantItems, setGrantItems] = useState([]);
  const [grantItemId, setGrantItemId] = useState('');
  const [grantQuantity, setGrantQuantity] = useState(1);
  const [grantRarities, setGrantRarities] = useState([]);
  const [grantRarityId, setGrantRarityId] = useState('');
  const [grantItemsLoading, setGrantItemsLoading] = useState(true);
  const [grantItemsError, setGrantItemsError] = useState('');
  const [grantPickerOpen, setGrantPickerOpen] = useState(false);
  const grantPickerRef = useRef(null);
  const [inventory, setInventory] = useState({
    capacity: 25,
    items: [],
  });
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [inventoryReloadVersion, setInventoryReloadVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState('');
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      250,
    );
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let current = true;
    Promise.all([getItems(), getItemTypes(), getRarity()]).then(([
      items,
      itemTypes,
      rarities,
    ]) => {
      if (!current) return;
      const types = new Map((itemTypes || []).map(type => [
        String(read(type, 'id', 'Id') || ''),
        {
          isStackable: Boolean(read(type, 'isStackable', 'IsStackable')),
          name: String(read(type, 'name', 'Name') || 'Items'),
        },
      ]));
      const options = (items || []).map(item => ({
        id: String(read(item, 'id', 'Id') || ''),
        name: String(read(item, 'name', 'Name') || ''),
        imageId: String(read(item, 'imageId', 'ImageId') || ''),
        typeName: types.get(String(
          read(item, 'typeId', 'TypeId') || '',
        ))?.name || 'Items',
        isStackable: types.get(String(
          read(item, 'typeId', 'TypeId') || '',
        ))?.isStackable || false,
        rarityId: String(read(item, 'rarityId', 'RarityId') || ''),
      })).filter(item => item.id && item.name)
        .sort((left, right) => left.name.localeCompare(right.name));
      setGrantItems(options);
      setGrantRarities((rarities || []).map(rarity => ({
        id: String(read(rarity, 'id', 'Id') || ''),
        name: String(read(rarity, 'name', 'Name') || ''),
      })).filter(rarity => rarity.id && rarity.name));
      setGrantItemId(selected => (
        options.some(item => item.id === selected)
          ? selected
          : ''
      ));
    }).catch(error => {
      if (!current) return;
      setGrantItemsError(
        error?.message || 'Items could not be loaded.',
      );
    }).finally(() => {
      if (current) setGrantItemsLoading(false);
    });
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError('');

    getAdminUsers({
      search: debouncedSearch,
      offset: 0,
      pageSize: PAGE_SIZE,
    }).then(value => {
      if (!current) return;
      const page = normalizePage(value);
      setUsers(page.users);
      setTotalCount(page.totalCount);
      setOverallCount(page.overallCount);
      setSelectedId(selected => (
        page.users.some(user => user.id === selected)
          ? selected
          : page.users[0]?.id || ''
      ));
    }).catch(error => {
      if (!current) return;
      setUsers([]);
      setTotalCount(0);
      setOverallCount(0);
      setSelectedId('');
      setLoadError(error?.message || 'Users could not be loaded.');
    }).finally(() => {
      if (current) setLoading(false);
    });

    return () => {
      current = false;
    };
  }, [debouncedSearch, reloadVersion]);

  useEffect(() => {
    let current = true;
    if (!selectedId) {
      setInventory({ capacity: 25, items: [] });
      setInventoryLoading(false);
      setInventoryError('');
      return () => {
        current = false;
      };
    }

    setInventoryLoading(true);
    setInventoryError('');
    getAdminUserInventory(selectedId).then(value => {
      if (current) setInventory(normalizeInventory(value));
    }).catch(error => {
      if (current) {
        setInventory({ capacity: 25, items: [] });
        setInventoryError(
          error?.message || 'The Pack could not be loaded.',
        );
      }
    }).finally(() => {
      if (current) setInventoryLoading(false);
    });

    return () => {
      current = false;
    };
  }, [inventoryReloadVersion, selectedId]);

  const selectedUser = useMemo(
    () => users.find(user => user.id === selectedId) || null,
    [selectedId, users],
  );

  useEffect(() => {
    if (!selectedUser) return;
    setDraft({
      email: selectedUser.email,
      userRole: selectedUser.userRole,
    });
  }, [selectedUser]);

  useEffect(() => {
    setNewPassword('');
    setConfirmPassword('');
    setActionError('');
    setMessage('');
  }, [selectedId]);

  const loadMore = async () => {
    setLoadingMore(true);
    setLoadError('');
    try {
      const page = normalizePage(await getAdminUsers({
        search: debouncedSearch,
        offset: users.length,
        pageSize: PAGE_SIZE,
      }));
      setUsers(current => [
        ...current,
        ...page.users.filter(next => (
          !current.some(existing => existing.id === next.id)
        )),
      ]);
      setTotalCount(page.totalCount);
      setOverallCount(page.overallCount);
    } catch (error) {
      setLoadError(error?.message || 'More users could not be loaded.');
    } finally {
      setLoadingMore(false);
    }
  };

  const saveAccount = async event => {
    event.preventDefault();
    if (!selectedUser) return;

    setBusy('account');
    setActionError('');
    setMessage('');
    try {
      const updated = normalizeUser(await updateAdminUser(
        selectedUser.id,
        {
          email: draft.email.trim(),
          userRole: Number(draft.userRole),
        },
      ));
      setUsers(current => current.map(user => (
        user.id === updated.id ? updated : user
      )));
      setMessage('Account settings saved.');
    } catch (error) {
      setActionError(error?.message || 'Account settings could not be saved.');
    } finally {
      setBusy('');
    }
  };

  const savePassword = async event => {
    event.preventDefault();
    if (!selectedUser) return;
    if (newPassword !== confirmPassword) {
      setActionError('The new passwords do not match.');
      return;
    }

    setBusy('password');
    setActionError('');
    setMessage('');
    try {
      await updateAdminUserPassword(selectedUser.id, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setMessage('Password changed.');
    } catch (error) {
      setActionError(error?.message || 'The password could not be changed.');
    } finally {
      setBusy('');
    }
  };

  const grantItem = async event => {
    event.preventDefault();
    if (!selectedUser || !grantItemId) return;

    const selectedItem = grantItems.find(item => item.id === grantItemId);
    setBusy('item');
    setActionError('');
    setMessage('');
    try {
      await grantAdminUserItem(
        selectedUser.id,
        grantItemId,
        Number(grantQuantity),
        selectedItem?.isStackable
          ? ''
          : selectedItem?.rarityId || grantRarityId,
      );
      setMessage(
        `${grantQuantity} × ${selectedItem?.name || 'item'} added to ${selectedUser.name}'s Pack.`,
      );
      setInventoryReloadVersion(value => value + 1);
    } catch (error) {
      setActionError(error?.message || 'The item could not be given.');
    } finally {
      setBusy('');
    }
  };

  const savePackItem = async (item, quantity) => {
    if (!selectedUser) return;
    if (quantity === 0 && !window.confirm(
      `Remove ${item.itemName} from ${selectedUser.name}'s Pack?`,
    )) {
      return;
    }

    setBusy(`pack-${item.x}-${item.y}`);
    setActionError('');
    setMessage('');
    try {
      const updated = await updateAdminUserInventoryItem(
        selectedUser.id,
        item.x,
        item.y,
        Number(quantity),
      );
      setInventory(normalizeInventory(updated));
      setMessage(quantity === 0
        ? `${item.itemName} removed from ${selectedUser.name}'s Pack.`
        : `${item.itemName} quantity changed to ${quantity}.`);
    } catch (error) {
      setActionError(error?.message || 'The Pack item could not be updated.');
    } finally {
      setBusy('');
    }
  };

  const selectedState = stateFor(draft.userRole);
  const selectedGrantItem = grantItems.find(
    item => item.id === grantItemId,
  );
  const grantNeedsRarity = Boolean(
    selectedGrantItem
    && !selectedGrantItem.isStackable
    && !selectedGrantItem.rarityId,
  );
  const grantPickerOptions = useMemo(() => grantItems.map(item => ({
    value: item.id,
    label: item.name,
    group: item.typeName,
    meta: item.isStackable ? 'Stackable · up to 20' : 'One item',
    keywords: [item.id],
  })), [grantItems]);
  const hasMore = users.length < totalCount;

  return (
    <section
      aria-busy={loading || Boolean(busy)}
      aria-labelledby="admin-users-title"
      className="admin-settingspage admin-users"
    >
      <aside aria-label="User list" className="admin_list admin-users__list">
        <header className="admin-users__list-header">
          <div>
            <h1 id="admin-users-title">Users</h1>
            <p>Alpha access and account support.</p>
          </div>
          <label htmlFor="admin-user-search">
            Search username or email
            <input
              autoComplete="off"
              id="admin-user-search"
              onChange={event => setSearch(event.target.value)}
              placeholder="Try a name, email, or close spelling"
              type="search"
              value={search}
            />
          </label>
        </header>

        {loading && <p role="status">Loading users…</p>}
        {!loading && loadError && users.length === 0 && (
          <div className="admin-users__state" role="alert">
            <p>{loadError}</p>
            <button
              className="button-secondary"
              onClick={() => setReloadVersion(value => value + 1)}
              type="button"
            >
              Try again
            </button>
          </div>
        )}
        {!loading && !loadError && users.length === 0 && (
          <p className="admin-users__state" role="status">
            {debouncedSearch
              ? 'No users match this search.'
              : 'No user accounts yet.'}
          </p>
        )}

        {users.length > 0 && (
          <ul className="admin-users__entries">
            {users.map(user => {
              const accountState = stateFor(user.userRole);
              return (
                <li key={user.id}>
                  <button
                    aria-pressed={selectedId === user.id}
                    className={selectedId === user.id ? 'is-active' : ''}
                    onClick={() => setSelectedId(user.id)}
                    type="button"
                  >
                    <span>{user.name}</span>
                    <small>{user.email}</small>
                    <small className={user.canPlay
                      ? 'admin-users__play-state is-playable'
                      : 'admin-users__play-state'}
                    >
                      {accountState.label}
                    </small>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <footer className="admin-users__list-footer">
          <p aria-live="polite">
            Showing {users.length} of {totalCount} users
            {debouncedSearch && overallCount !== totalCount
              ? ` (${overallCount} total)`
              : ''}
          </p>
          {hasMore && (
            <button
              className="button-secondary"
              disabled={loadingMore}
              onClick={loadMore}
              type="button"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
          {loadError && users.length > 0 && (
            <p className="admin-users__error" role="alert">{loadError}</p>
          )}
        </footer>
      </aside>

      <main className="admin_details admin-users__detail">
        {!selectedUser && !loading && (
          <p className="admin-users__state" role="status">
            Select a user to manage the account.
          </p>
        )}
        {selectedUser && (
          <>
            <header className="admin-users__detail-header">
              <div>
                <p className="admin-users__eyebrow">User account</p>
                <h2>{selectedUser.name}</h2>
                <p>{selectedUser.id}</p>
              </div>
              <span className={selectedUser.canPlay
                ? 'admin-users__badge is-playable'
                : 'admin-users__badge'}
              >
                {selectedUser.canPlay ? 'Can play' : 'Locked'}
              </span>
            </header>

            {(message || actionError) && (
              <p
                className={actionError
                  ? 'admin-users__feedback is-error'
                  : 'admin-users__feedback'}
                role={actionError ? 'alert' : 'status'}
              >
                {actionError || message}
              </p>
            )}

            <form
              className="admin-users__card"
              onSubmit={saveAccount}
            >
              <header>
                <h3>Account access</h3>
                <p>Email and the existing role decide account access.</p>
              </header>
              <div className="admin-users__form-grid">
                <label htmlFor="admin-user-email">
                  Email
                  <input
                    autoComplete="email"
                    id="admin-user-email"
                    onChange={event => setDraft(current => ({
                      ...current,
                      email: event.target.value,
                    }))}
                    required
                    type="email"
                    value={draft.email}
                  />
                </label>
                <label htmlFor="admin-user-role">
                  Account state
                  <select
                    id="admin-user-role"
                    onChange={event => setDraft(current => ({
                      ...current,
                      userRole: Number(event.target.value),
                    }))}
                    value={draft.userRole}
                  >
                    {ACCOUNT_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <p
                className={draft.userRole === 666
                  ? 'admin-users__role-help is-warning'
                  : 'admin-users__role-help'}
              >
                {selectedState.detail}
              </p>
              <button
                className="button-primary"
                disabled={Boolean(busy)}
                type="submit"
              >
                {busy === 'account' ? 'Saving…' : 'Save account'}
              </button>
            </form>

            <form
              className="admin-users__card"
              onSubmit={savePassword}
            >
              <header>
                <h3>Change password</h3>
                <p>Set a new password without displaying the old one.</p>
              </header>
              <div className="admin-users__form-grid">
                <label htmlFor="admin-user-password">
                  New password
                  <input
                    autoComplete="new-password"
                    id="admin-user-password"
                    minLength="6"
                    onChange={event => setNewPassword(event.target.value)}
                    required
                    type="password"
                    value={newPassword}
                  />
                </label>
                <label htmlFor="admin-user-password-confirm">
                  Confirm new password
                  <input
                    autoComplete="new-password"
                    id="admin-user-password-confirm"
                    minLength="6"
                    onChange={event => setConfirmPassword(event.target.value)}
                    required
                    type="password"
                    value={confirmPassword}
                  />
                </label>
              </div>
              <small>
                At least 6 characters using two of: lowercase, uppercase,
                numbers, or symbols.
              </small>
              <button
                className="button-secondary"
                disabled={Boolean(busy)}
                type="submit"
              >
                {busy === 'password' ? 'Changing…' : 'Change password'}
              </button>
            </form>

            <form
              className="admin-users__card"
              onSubmit={grantItem}
            >
              <header>
                <h3>Give item</h3>
                <p>Add an item directly to this user's Pack.</p>
              </header>
              {grantItemsLoading && <p role="status">Loading items…</p>}
              {grantItemsError && (
                <p className="admin-users__error" role="alert">
                  {grantItemsError}
                </p>
              )}
              {!grantItemsLoading && !grantItemsError
                && grantItems.length === 0 && (
                  <p role="status">No items are available.</p>
              )}
              {grantItems.length > 0 && (
                <div className="admin-users__form-grid">
                  <fieldset className="admin-users__item-picker-field">
                    <legend>Item</legend>
                    <button
                      aria-haspopup="dialog"
                      className="button-secondary admin-users__item-picker"
                      onClick={() => setGrantPickerOpen(true)}
                      ref={grantPickerRef}
                      type="button"
                    >
                      {selectedGrantItem?.name || 'Choose item…'}
                      <span aria-hidden="true">⌕</span>
                    </button>
                  </fieldset>
                  <label htmlFor="admin-user-grant-quantity">
                    Quantity
                    <input
                      id="admin-user-grant-quantity"
                      max={selectedGrantItem?.isStackable ? 20 : 1}
                      min="1"
                      onChange={event => setGrantQuantity(
                        Number(event.target.value),
                      )}
                      required
                      type="number"
                      value={grantQuantity}
                    />
                  </label>
                  {selectedGrantItem && !selectedGrantItem.isStackable && (
                    <label htmlFor="admin-user-grant-rarity">
                      Rarity
                      <select
                        disabled={Boolean(selectedGrantItem.rarityId)}
                        id="admin-user-grant-rarity"
                        onChange={event => setGrantRarityId(event.target.value)}
                        required={grantNeedsRarity}
                        value={selectedGrantItem.rarityId || grantRarityId}
                      >
                        {grantNeedsRarity && (
                          <option value="">Choose rarity…</option>
                        )}
                        {grantRarities.map(rarity => (
                          <option key={rarity.id} value={rarity.id}>
                            {rarity.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              )}
              {grantPickerOpen && (
                <SearchPickerDialog
                  description="Fuzzy search by item name, type, or id."
                  onApply={values => {
                    const nextId = values[0] || '';
                    setGrantItemId(nextId);
                    setGrantRarityId('');
                    if (!grantItems.find(item => item.id === nextId)
                      ?.isStackable) {
                      setGrantQuantity(1);
                    }
                  }}
                  onClose={() => setGrantPickerOpen(false)}
                  options={grantPickerOptions}
                  returnFocusRef={grantPickerRef}
                  searchLabel="Search by item name, type, or id"
                  selectedValues={grantItemId ? [grantItemId] : []}
                  title="Choose item to give"
                />
              )}
              <small>
                Stackable items can be given up to 20 at a time.
                Equipment is given one at a time.
              </small>
              <button
                className="button-secondary"
                disabled={Boolean(busy)
                  || !grantItemId
                  || (grantNeedsRarity && !grantRarityId)}
                type="submit"
              >
                {busy === 'item' ? 'Adding…' : 'Add to Pack'}
              </button>
            </form>

            <section
              aria-labelledby="admin-user-pack-title"
              className="admin-users__card"
            >
              <header>
                <h3 id="admin-user-pack-title">Pack</h3>
                <p>
                  {inventory.items.length} of {inventory.capacity} slots used.
                  Change stack quantities or remove items.
                </p>
              </header>
              {inventoryLoading && <p role="status">Loading Pack…</p>}
              {inventoryError && (
                <div className="admin-users__state" role="alert">
                  <p>{inventoryError}</p>
                  <button
                    className="button-secondary"
                    onClick={() => setInventoryReloadVersion(
                      value => value + 1,
                    )}
                    type="button"
                  >
                    Try again
                  </button>
                </div>
              )}
              {!inventoryLoading && !inventoryError
                && inventory.items.length === 0 && (
                  <p className="admin-users__state" role="status">
                    This Pack is empty.
                  </p>
              )}
              {inventory.items.length > 0 && (
                <ul className="admin-users__pack">
                  {inventory.items.map(item => (
                    <PackItemEditor
                      busy={Boolean(busy)}
                      item={item}
                      key={`${item.x}:${item.y}`}
                      onSave={savePackItem}
                    />
                  ))}
                </ul>
              )}
            </section>

            <section
              aria-label="Account activity"
              className="admin-users__card"
            >
              <header>
                <h3>Account activity</h3>
              </header>
              <dl className="admin-users__facts">
                <div>
                  <dt>XP</dt>
                  <dd>{selectedUser.xp.toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Last login</dt>
                  <dd>{formatTimestamp(selectedUser.lastLogin)}</dd>
                </div>
                <div>
                  <dt>Last activity</dt>
                  <dd>{formatTimestamp(selectedUser.lastActivity)}</dd>
                </div>
              </dl>
            </section>
          </>
        )}
      </main>
    </section>
  );
}
